import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  AppointmentResponse,
  CreateAppointmentRequest,
  DaySlotsResponse,
  NextAvailableResponse,
  PatientSummaryResponse,
  PolicyOutcomeResponse,
  ServiceAreaResponse,
  ServiceResponse,
  SlotResponse,
  WorkingDoctorResponse,
} from '../../api/types';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';
import { clinicDay, clinicTime, clinicToday, longDate } from './clinicTime';

interface BookingModalProps {
  readonly clinicId: string;
  readonly branchId: string;
  readonly on: string;
  /** Pre-selected when the desk opened this from a patient's profile. */
  readonly patient?: PatientSummaryResponse | null;
  readonly onClose: () => void;
  readonly onBooked: (appointment: AppointmentResponse) => void;
}

/**
 * Booking, as reception does it (FR-402, FR-303, RULE-009).
 *
 * The order of the fields is the order of the conversation at the desk: who, what, then when. The
 * slot list is the availability engine's answer, so a time that appears here is a time all four
 * calendars agree on — and a time that does not appear is not a time to be typed in anyway.
 *
 * The one deliberate escape hatch is the override. When the policy engine objects, the reason box
 * appears with the objection written above it, so overriding is a decision somebody made with the
 * rule in front of them rather than a field they filled in to make an error go away.
 *
 * The clinical safety gate (no patch test, no intake) is not asked here (CR-26). Every new patient
 * tripped it, and reception cannot read the record it is about (FR-405). The server leaves the
 * warning unanswered on the booking, and the treating doctor sees the findings before the session.
 */
export function BookingModal({
  clinicId,
  branchId,
  on,
  patient = null,
  onClose,
  onBooked,
}: BookingModalProps) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();

  const [search, setSearch] = useState('');
  const [chosenPatient, setChosenPatient] = useState<PatientSummaryResponse | null>(patient);
  const [serviceId, setServiceId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [employmentId, setEmploymentId] = useState('');
  const [date, setDate] = useState(on);
  const [slot, setSlot] = useState<SlotResponse | null>(null);
  const [note, setNote] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const patients = useApiQuery<PatientSummaryResponse[]>(
    queryKeys.patients(clinicId, search),
    `/clinics/${clinicId}/patients?q=${encodeURIComponent(search)}`,
    { enabled: search.trim().length >= 2 },
  );

  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId),
    `/clinics/${clinicId}/catalog/services`,
  );

  /** The doctors approved for a service (CR-21). Empty means any doctor may deliver it. */
  const approvedFor = (id: string): string[] =>
    (services.data?.find((service) => service.id === id)?.doctorEmploymentIds ?? []).filter(
      (employment): employment is string => Boolean(employment),
    );

  const areas = useApiQuery<ServiceAreaResponse[]>(
    queryKeys.serviceAreas(clinicId, serviceId),
    `/clinics/${clinicId}/catalog/services/${serviceId}/areas`,
    { enabled: Boolean(serviceId) },
  );

  const availabilityQuery = useMemo(() => {
    const params = new URLSearchParams({ branchId, serviceId, from: date, to: date });
    if (areaId) {
      params.set('serviceAreaId', areaId);
    }
    if (employmentId) {
      params.set('employmentId', employmentId);
    }
    return params.toString();
  }, [branchId, serviceId, areaId, employmentId, date]);

  const slots = useApiQuery<DaySlotsResponse[]>(
    queryKeys.availability(clinicId, availabilityQuery),
    `/clinics/${clinicId}/availability?${availabilityQuery}`,
    { enabled: Boolean(serviceId) },
  );

  // Fetched on demand rather than with the slot list: searching sixty days forward is work nobody
  // asked for until they press the button.
  const nextAvailableQuery = useMemo(() => {
    const params = new URLSearchParams({ branchId, serviceId });
    if (areaId) {
      params.set('serviceAreaId', areaId);
    }
    if (employmentId) {
      params.set('employmentId', employmentId);
    }
    return params.toString();
  }, [branchId, serviceId, areaId, employmentId]);

  const nextAvailable = useApiQuery<NextAvailableResponse>(
    queryKeys.availability(clinicId, `next?${nextAvailableQuery}`),
    `/clinics/${clinicId}/availability/next?${nextAvailableQuery}`,
    { enabled: false },
  );

  const working = useApiQuery<WorkingDoctorResponse[]>(
    queryKeys.workingDoctors(clinicId, branchId, date),
    `/clinics/${clinicId}/doctors/working?branchId=${branchId}&on=${date}`,
  );

  const preview = useApiMutation<PolicyOutcomeResponse, CreateAppointmentRequest>((body) => ({
    path: `/clinics/${clinicId}/appointments/preview`,
    options: { method: 'POST', body },
  }));

  const book = useApiMutation<AppointmentResponse, CreateAppointmentRequest>(
    (body) => ({ path: `/clinics/${clinicId}/appointments`, options: { method: 'POST', body } }),
    {
      invalidates: [['clinics', clinicId, 'appointments', 'board', branchId]],
      onSuccess: () => undefined,
    },
  );

  const day = slots.data?.[0];
  const readyToBook = Boolean(chosenPatient && serviceId && slot);
  const violations = preview.data?.violations ?? [];
  const needsOverride = preview.data ? !preview.data.withinPolicy : false;

  const request = (): CreateAppointmentRequest | null => {
    if (!chosenPatient || !serviceId || !slot?.startsAt || !slot.employmentId) {
      return null;
    }
    return {
      branchId,
      patientMembershipId: chosenPatient.id ?? '',
      serviceId,
      ...(areaId ? { serviceAreaId: areaId } : {}),
      employmentId: slot.employmentId,
      ...(slot.roomId ? { roomId: slot.roomId } : {}),
      startsAt: slot.startsAt,
      ...(note ? { note } : {}),
      ...(overrideReason ? { overrideReason } : {}),
    };
  };

  const chooseSlot = (candidate: SlotResponse) => {
    setSlot(candidate);
    setOverrideReason('');
    const body = {
      branchId,
      patientMembershipId: chosenPatient?.id ?? '',
      serviceId,
      ...(areaId ? { serviceAreaId: areaId } : {}),
      employmentId: candidate.employmentId ?? '',
      startsAt: candidate.startsAt ?? '',
    } as CreateAppointmentRequest;
    if (chosenPatient) {
      preview.mutate(body);
    }
  };

  const submit = () => {
    const body = request();
    if (body) {
      book.mutate(body, { onSuccess: (created) => onBooked(created) });
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('board.book')}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{t('board.book')}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </header>

        <div className="modal__body">
          <section className="section">
            <h3 className="section__title">{t('booking.who')}</h3>
            {chosenPatient ? (
              <div className="switch-row">
                <span>
                  <strong>{chosenPatient.fullName}</strong>{' '}
                  <span className="ltr">{chosenPatient.phone}</span>
                  {(chosenPatient.noShowCount ?? 0) > 0 ? (
                    <span className="badge badge--warning">
                      {t('booking.noShows', { count: chosenPatient.noShowCount ?? 0 })}
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="button button--ghost button--compact"
                  onClick={() => setChosenPatient(null)}
                >
                  {t('common.change')}
                </button>
              </div>
            ) : (
              <>
                <label className="field">
                  <span className="field__label">{t('patients.search')}</span>
                  <input
                    className="field__input"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('patients.searchHint')}
                  />
                </label>
                <ul className="picker">
                  {(patients.data ?? []).map((row) => (
                    <li key={row.id}>
                      <button type="button" className="picker__row" onClick={() => setChosenPatient(row)}>
                        <span>{row.fullName}</span>
                        <span className="ltr">{row.phone}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="section">
            <h3 className="section__title">{t('booking.what')}</h3>
            <div className="form-grid">
              <label className="field">
                <span className="field__label">{t('booking.service')}</span>
                <select
                  className="field__input"
                  value={serviceId}
                  onChange={(event) => {
                    const next = event.target.value;
                    setServiceId(next);
                    setAreaId('');
                    setSlot(null);
                    // A doctor picked for the last service may not do this one (CR-21).
                    const approved = approvedFor(next);
                    if (employmentId && approved.length > 0 && !approved.includes(employmentId)) {
                      setEmploymentId('');
                    }
                  }}
                >
                  <option value="">{t('common.choose')}</option>
                  {(services.data ?? [])
                    .filter((service) => service.status === 'ACTIVE')
                    .map((service) => (
                      <option key={service.id} value={service.id}>
                        {localised.name(service)}
                      </option>
                    ))}
                </select>
              </label>

              {(areas.data ?? []).length > 0 ? (
                <label className="field">
                  <span className="field__label">{t('booking.area')}</span>
                  <select
                    className="field__input"
                    value={areaId}
                    onChange={(event) => {
                      setAreaId(event.target.value);
                      setSlot(null);
                    }}
                  >
                    <option value="">{t('booking.wholeService')}</option>
                    {(areas.data ?? []).map((area) => (
                      <option key={area.id} value={area.id}>
                        {localised.name(area)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </section>

          <section className="section">
            <h3 className="section__title">{t('booking.when')}</h3>
            <div className="toolbar">
              <label className="field">
                <span className="field__label">{t('board.date')}</span>
                <input
                  className="field__input"
                  type="date"
                  value={date}
                  min={clinicToday()}
                  onChange={(event) => {
                    setDate(event.target.value);
                    setSlot(null);
                  }}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('booking.doctor')}</span>
                <select
                  className="field__input"
                  value={employmentId}
                  onChange={(event) => {
                    setEmploymentId(event.target.value);
                    setSlot(null);
                  }}
                >
                  {/* "Any doctor" first, because it is what most patients say and it returns the
                      widest set of times. */}
                  <option value="">{t('booking.anyDoctor')}</option>
                  {(working.data ?? [])
                    .filter((doctor) => {
                      // Only the doctors who do this service (CR-21); none listed means any doctor.
                      const approved = approvedFor(serviceId);
                      return approved.length === 0 || approved.includes(doctor.employmentId ?? '');
                    })
                    .map((doctor) => (
                      <option key={doctor.employmentId} value={doctor.employmentId}>
                        {doctor.doctorName}
                      </option>
                    ))}
                </select>
              </label>
              <button
                type="button"
                className="button button--ghost"
                disabled={!serviceId || nextAvailable.isFetching}
                onClick={() => {
                  void nextAvailable.refetch().then(({ data }) => {
                    if (data?.slot?.startsAt) {
                      setDate(clinicDay(data.slot.startsAt));
                      chooseSlot(data.slot);
                    }
                  });
                }}
              >
                {t('booking.nextAvailable')}
              </button>
            </div>

            {!serviceId ? (
              <p className="field__hint">{t('booking.chooseServiceFirst')}</p>
            ) : day?.branchClosed ? (
              <p className="field__hint">{t('booking.branchClosed', { date: longDate(date, i18n.language) })}</p>
            ) : (day?.slots ?? []).length === 0 ? (
              <p className="field__hint">{t('booking.noSlots')}</p>
            ) : (
              <ul className="slots">
                {(day?.slots ?? []).map((candidate) => (
                  <li key={`${candidate.startsAt}-${candidate.employmentId}`}>
                    <button
                      type="button"
                      className={
                        slot?.startsAt === candidate.startsAt && slot?.employmentId === candidate.employmentId
                          ? 'slots__slot slots__slot--chosen'
                          : 'slots__slot'
                      }
                      onClick={() => chooseSlot(candidate)}
                    >
                      <strong>{clinicTime(candidate.startsAt)}</strong>
                      <span>{candidate.doctorName}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {violations.length > 0 ? (
            <div className="callout">
              <p>{t('booking.policyWarning')}</p>
              <ul>
                {violations.map((violation) => (
                  <li key={violation.rule}>{t(`booking.rules.${violation.rule}`, violation.message ?? '')}</li>
                ))}
              </ul>
              {needsOverride ? (
                <label className="field">
                  <span className="field__label">{t('booking.overrideReason')}</span>
                  <input
                    className="field__input"
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                    placeholder={t('booking.overrideHint')}
                  />
                  <span className="field__hint">{t('booking.overrideAudited')}</span>
                </label>
              ) : null}
            </div>
          ) : null}

          <label className="field">
            <span className="field__label">{t('booking.note')}</span>
            <input
              className="field__input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('booking.noteHint')}
            />
            <span className="field__hint">{t('booking.noteOperational')}</span>
          </label>

          {book.error ? <ErrorState error={book.error} /> : null}
        </div>

        <footer className="modal__footer">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={
              !readyToBook ||
              book.isPending ||
              (needsOverride && overrideReason.trim().length === 0)
            }
            onClick={submit}
          >
            {needsOverride ? t('booking.bookAnyway') : t('booking.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
}
